/**
 * Configuration for unit stat comparison
 * Defines which stats are better when higher vs lower, and their categories
 */

export type StatConfig = {
  statKey: string;
  higherIsBetter: boolean;
  label: string;
  category: string;
};

export const STAT_COMPARISON_CONFIG: StatConfig[] = [
  // Movement stats - Higher is better
  { statKey: "speed", higherIsBetter: true, label: "Speed", category: "Movement" },
  { statKey: "roadSpeed", higherIsBetter: true, label: "Road Speed", category: "Movement" },
  { statKey: "fuel", higherIsBetter: true, label: "Fuel", category: "Movement" },
  { statKey: "fuelMove", higherIsBetter: true, label: "Fuel Move", category: "Movement" },
  { statKey: "advancedDeployment", higherIsBetter: true, label: "Advanced Deployment", category: "Movement" },
  // Movement stats - Lower is better
  { statKey: "rotationTime", higherIsBetter: false, label: "Rotation Time", category: "Movement" },

  // Armor stats - Higher is better
  { statKey: "frontArmor", higherIsBetter: true, label: "Front Armor", category: "Armor" },
  { statKey: "sideArmor", higherIsBetter: true, label: "Side Armor", category: "Armor" },
  { statKey: "rearArmor", higherIsBetter: true, label: "Rear Armor", category: "Armor" },
  { statKey: "topArmor", higherIsBetter: true, label: "Top Armor", category: "Armor" },

  // Optics stats - Higher is better
  { statKey: "optics", higherIsBetter: true, label: "Optics", category: "Optics" },
  { statKey: "airOptics", higherIsBetter: true, label: "Air Optics", category: "Optics" },
  { statKey: "lowAltOptics", higherIsBetter: true, label: "Low Alt Optics", category: "Optics" },
  { statKey: "opticsRange", higherIsBetter: true, label: "Optics Range", category: "Optics" },
  { statKey: "airOpticsRange", higherIsBetter: true, label: "Air Optics Range", category: "Optics" },
  { statKey: "lowAltOpticsRange", higherIsBetter: true, label: "Low Alt Optics Range", category: "Optics" },

  // Flying stats - Higher is better
  { statKey: "ecm", higherIsBetter: false, label: "ECM", category: "Flying" },
  { statKey: "agility", higherIsBetter: true, label: "Agility", category: "Flying" },
  { statKey: "flyingAltitude", higherIsBetter: true, label: "Flying Altitude", category: "Flying" },
  // Flying stats - Lower is better
  { statKey: "travelTime", higherIsBetter: false, label: "Travel Time", category: "Flying" },

  // Misc stats
  { statKey: "commandPoints", higherIsBetter: false, label: "Command Points", category: "Misc" },
  { statKey: "maxDamage", higherIsBetter: true, label: "Max Damage", category: "Misc" },
  { statKey: "dangerousness", higherIsBetter: false, label: "Dangerousness", category: "Misc" },
  { statKey: "stealth", higherIsBetter: true, label: "Stealth", category: "Misc" },
  { statKey: "supply", higherIsBetter: true, label: "Supply", category: "Misc" },

  // Weapon stats - Higher is better
  { statKey: "weapon_penetration", higherIsBetter: true, label: "Penetration", category: "Weapon" },
  { statKey: "weapon_heDamage", higherIsBetter: true, label: "HE Damage", category: "Weapon" },
  { statKey: "weapon_suppress", higherIsBetter: true, label: "Suppress", category: "Weapon" },
  { statKey: "weapon_groundMaxRange", higherIsBetter: true, label: "Ground Max Range", category: "Weapon" },
  { statKey: "weapon_heliMaxRange", higherIsBetter: true, label: "Heli Max Range", category: "Weapon" },
  { statKey: "weapon_planeMaxRange", higherIsBetter: true, label: "Plane Max Range", category: "Weapon" },
  { statKey: "weapon_staticAccuracy", higherIsBetter: true, label: "Static Accuracy", category: "Weapon" },
  { statKey: "weapon_movingAccuracy", higherIsBetter: true, label: "Moving Accuracy", category: "Weapon" },
  { statKey: "weapon_rateOfFire", higherIsBetter: true, label: "Rate of Fire", category: "Weapon" },
  // Weapon stats - Lower is better
  { statKey: "weapon_aimingTime", higherIsBetter: false, label: "Aiming Time", category: "Weapon" },
  { statKey: "weapon_reloadTime", higherIsBetter: false, label: "Reload Time", category: "Weapon" },
  { statKey: "weapon_supplyCost", higherIsBetter: false, label: "Supply Cost", category: "Weapon" },
  { statKey: "weapon_groundMinRange", higherIsBetter: false, label: "Ground Min Range", category: "Weapon" },
  { statKey: "weapon_heliMinRange", higherIsBetter: false, label: "Heli Min Range", category: "Weapon" },
  { statKey: "weapon_planeMinRange", higherIsBetter: false, label: "Plane Min Range", category: "Weapon" },
];

/**
 * Get comparison config for a specific stat
 */
export function getStatConfig(statKey: string): StatConfig | undefined {
  return STAT_COMPARISON_CONFIG.find((config) => config.statKey === statKey);
}

/**
 * Get all stats for a specific category
 */
export function getStatsByCategory(category: string): StatConfig[] {
  return STAT_COMPARISON_CONFIG.filter((config) => config.category === category);
}

/**
 * Get all unique categories
 */
export function getCategories(): string[] {
  return Array.from(new Set(STAT_COMPARISON_CONFIG.map((config) => config.category)));
}

/**
 * Get all stat keys
 */
export function getAllStatKeys(): string[] {
  return STAT_COMPARISON_CONFIG.map((config) => config.statKey);
}

/**
 * Get all weapon stat keys
 */
export function getWeaponStatKeys(): string[] {
  return STAT_COMPARISON_CONFIG.filter((config) => config.category === "Weapon").map((config) => config.statKey);
}

/**
 * Check if a stat key is a weapon stat
 */
export function isWeaponStat(statKey: string): boolean {
  return statKey.startsWith("weapon_");
}

